import { FC, useMemo, type ReactNode } from 'react';
import { Table, Button, Popconfirm, Space } from 'antd';
import { CopyOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { TableCard } from '../EU6Calculator.styles';
import { APP_SHELL_THEME } from '@/styles/appShellTheme';
import { RateCard, Currency } from '../EU6Calculator.types';
import { getCurrencySymbol } from '@/constants/currency';
import { DELETE_ACTION_COLOR } from '@/constants/colors';
import { NAMESPACE } from '@/constants';

const TABLE_ACTION_BTN_STYLE = { color: APP_SHELL_THEME.textBody } as const;

interface RatesTableProps {
  rateCards: RateCard[];
  onDelete: (id: string) => void;
  onEdit?: (rate: RateCard) => void;
  onDuplicate?: (rate: RateCard) => void;
  currency: Currency;
  loading?: boolean;
  showActions?: boolean;
  showSummary?: boolean;
}

function sumRateCards(cards: RateCard[], pick: (card: RateCard) => number): number {
  return cards.reduce((sum, card) => {
    const value = pick(card);
    return sum + (Number.isFinite(value) ? value : 0);
  }, 0);
}

export const RatesTable: FC<RatesTableProps> = ({
  rateCards,
  onDelete,
  onEdit,
  onDuplicate,
  currency,
  loading = false,
  showActions = true,
  showSummary = true
}) => {
  const { t } = useTranslation(NAMESPACE.COMMON);

  const formatCurrency = (value: unknown): string => {
    const num = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
    if (!Number.isFinite(num)) return '—';
    return `${getCurrencySymbol(currency)}${num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const formatCurrencyForRow = (value: unknown, rowCurrency: unknown): string => {
    const num = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
    if (!Number.isFinite(num)) return '—';
    const code = typeof rowCurrency === 'string' ? rowCurrency : currency;
    return `${getCurrencySymbol(code)}${num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const formatNumber = (value: unknown): string => {
    const num = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
    if (!Number.isFinite(num)) return '—';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatPercent = (value: unknown): string => {
    const num = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
    if (!Number.isFinite(num)) return '—';
    return `${num.toFixed(2)}%`;
  };

  const columns: ColumnsType<RateCard> = useMemo(
    () => [
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      width: 100
    },
    {
      title: 'Channel',
      dataIndex: 'channel',
      key: 'channel',
      width: 100
    },
    {
      title: 'Publisher',
      dataIndex: 'publisher',
      key: 'publisher',
      width: 130
    },
    {
      title: 'Format',
      dataIndex: 'format',
      key: 'format',
      width: 100
    },
    {
      title: '% Delivery',
      dataIndex: 'deliveryPercentage',
      key: 'deliveryPercentage',
      width: 130,
      className: 'numeric',
      render: (value, record) => (
        <span>
          {formatPercent(value)}
          {Number.isFinite(record.grossBudgetPlan) && record.grossBudgetPlan > 0 && (
            <div style={{ fontSize: 11, color: APP_SHELL_THEME.textMuted }}>{formatCurrency(record.grossBudgetPlan)}</div>
          )}
        </span>
      )
    },
    {
      title: 'Currency',
      dataIndex: 'rowCurrency',
      key: 'rowCurrency',
      width: 90
    },
    {
      title: 'Buy CPM',
      dataIndex: 'buyCPM',
      key: 'buyCPM',
      width: 100,
      className: 'numeric',
      render: (value, record) => formatCurrencyForRow(value, (record as any)?.rowCurrency)
    },
    {
      /* Shown so a row with no DSP stands out: lines priced from the package
         take their platform from it. */
      title: 'DSP',
      dataIndex: 'dspFeeName',
      key: 'dspFeeName',
      width: 130,
      render: (value?: string) => value || '—'
    },
    {
      title: 'DSP Fee %',
      dataIndex: 'dspFeePercentage',
      key: 'dspFeePercentage',
      width: 90,
      className: 'numeric',
      render: formatPercent
    },
    {
      title: 'Gross Budget',
      dataIndex: 'grossBudgetPlan',
      key: 'grossBudgetPlan',
      width: 120,
      className: 'numeric',
      render: formatCurrency
    },
    {
      title: 'Net Budget',
      dataIndex: 'netBudgetPlan',
      key: 'netBudgetPlan',
      width: 120,
      className: 'numeric',
      render: formatCurrency
    },
    {
      title: 'Impressions',
      dataIndex: 'impressions',
      key: 'impressions',
      width: 120,
      className: 'numeric',
      render: (value: number) => value.toLocaleString('en-US', { maximumFractionDigits: 0 })
    },
    {
      title: 'Publisher Spend',
      dataIndex: 'publisherSpendPlan',
      key: 'publisherSpendPlan',
      width: 130,
      className: 'numeric',
      render: formatCurrency
    },
    {
      title: 'DSP Fee',
      dataIndex: 'dspFeeValuePlan',
      key: 'dspFeeValuePlan',
      width: 100,
      className: 'numeric',
      render: formatCurrency
    },
    {
      title: 'Total Media Spend',
      dataIndex: 'totalMediaSpendPlan',
      key: 'totalMediaSpendPlan',
      width: 140,
      className: 'numeric',
      render: formatCurrency
    },
    {
      title: 'Allocated Hard Cost',
      dataIndex: 'allocatedHardCostPlan',
      key: 'allocatedHardCostPlan',
      width: 140,
      className: 'numeric',
      render: formatCurrency
    },
    {
      title: t('grossMargin'),
      dataIndex: 'grossMarginPlan',
      key: 'grossMarginPlan',
      width: 120,
      className: 'numeric',
      render: formatCurrency
    },
    {
      title: t('rebatePercent'),
      dataIndex: 'effectiveTradingDealPercentage',
      key: 'effectiveTradingDealPercentage',
      width: 90,
      className: 'numeric',
      render: formatPercent
    },
    {
      title: t('rebate'),
      dataIndex: 'tradingDealAmountPlan',
      key: 'tradingDealAmountPlan',
      width: 110,
      className: 'numeric',
      render: formatCurrency
    },
    {
      title: t('grossProfit'),
      dataIndex: 'grossProfitPlan',
      key: 'grossProfitPlan',
      width: 120,
      className: 'numeric',
      render: formatCurrency
    },
    {
      title: t('grossMarginPercent'),
      dataIndex: 'marginPercentage',
      key: 'marginPercentage',
      width: 110,
      className: 'numeric',
      render: formatPercent
    },
    {
      title: t('grossProfitPercent'),
      dataIndex: 'profitPercentage',
      key: 'profitPercentage',
      width: 110,
      className: 'numeric',
      render: formatPercent
    },
    ...(showActions
      ? ([
          {
            title: 'Actions',
            dataIndex: 'actions',
            key: 'actions',
            className: 'actions',
            width: 260,
            fixed: 'right',
            align: 'center',
            render: (_, record) => (
              <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
                {onEdit && (
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    size="small"
                    style={TABLE_ACTION_BTN_STYLE}
                    onClick={() => onEdit(record)}
                  >
                    Edit
                  </Button>
                )}
                {onDuplicate && (
                  <Button
                    type="text"
                    icon={<CopyOutlined />}
                    size="small"
                    style={TABLE_ACTION_BTN_STYLE}
                    onClick={() => onDuplicate(record)}
                  >
                    Duplicate
                  </Button>
                )}
                <Popconfirm
                  title={
                    <span style={{ color: DELETE_ACTION_COLOR }}>Delete this rate?</span>
                  }
                  description="Are you sure you want to delete this rate from the plan?"
                  onConfirm={() => onDelete(record.id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    size="small"
                    style={{ color: DELETE_ACTION_COLOR }}
                  >
                    Delete
                  </Button>
                </Popconfirm>
              </Space>
            )
          }
        ] as ColumnsType<RateCard>)
      : [])
  ],
    [t, showActions, onDuplicate, onEdit, onDelete, formatCurrency, formatCurrencyForRow, formatPercent, formatNumber]
  );

  const totals = useMemo(() => {
    const totalNetBudgetPlan = sumRateCards(rateCards, (c) => c.netBudgetPlan);
    const totalGrossMarginPlan = sumRateCards(rateCards, (c) => c.grossMarginPlan);
    const totalGrossProfitPlan = sumRateCards(rateCards, (c) => c.grossProfitPlan);

    return {
      deliveryPercentage: sumRateCards(rateCards, (c) => Number(c.deliveryPercentage) || 0),
      grossBudgetPlan: sumRateCards(rateCards, (c) => c.grossBudgetPlan),
      netBudgetPlan: totalNetBudgetPlan,
      impressions: sumRateCards(rateCards, (c) => c.impressions),
      publisherSpendPlan: sumRateCards(rateCards, (c) => c.publisherSpendPlan),
      dspFeeValuePlan: sumRateCards(rateCards, (c) => c.dspFeeValuePlan),
      totalMediaSpendPlan: sumRateCards(rateCards, (c) => c.totalMediaSpendPlan),
      allocatedHardCostPlan: sumRateCards(rateCards, (c) => c.allocatedHardCostPlan),
      grossMarginPlan: totalGrossMarginPlan,
      tradingDealAmountPlan: sumRateCards(rateCards, (c) => c.tradingDealAmountPlan),
      grossProfitPlan: totalGrossProfitPlan,
      blendedMarginPercentage:
        totalNetBudgetPlan > 0 ? (totalGrossMarginPlan / totalNetBudgetPlan) * 100 : 0,
      blendedProfitPercentage:
        totalNetBudgetPlan > 0 ? (totalGrossProfitPlan / totalNetBudgetPlan) * 100 : 0
    };
  }, [rateCards]);

  const renderSummary = () => {
    if (!showSummary || rateCards.length === 0) return null;

    let cellIndex = 0;
    const nextIndex = () => cellIndex++;
    const totalCell = (content: ReactNode) => (
      <Table.Summary.Cell index={nextIndex()} className="numeric" align="right">
        <strong>{content}</strong>
      </Table.Summary.Cell>
    );

    return (
      <Table.Summary fixed>
        <Table.Summary.Row className="rates-table-total-row">
          <Table.Summary.Cell index={nextIndex()} colSpan={4}>
            <strong>Total</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={nextIndex()} className="numeric" align="right">
            <strong>{formatPercent(totals.deliveryPercentage)}</strong>
            {totals.grossBudgetPlan > 0 && (
              <div style={{ fontSize: 11, color: APP_SHELL_THEME.textMuted }}>{formatCurrency(totals.grossBudgetPlan)}</div>
            )}
          </Table.Summary.Cell>
          {/* Currency, Buy CPM, DSP and DSP Fee % have no total. One blank per
              column: the DSP column arrived without one, and every total
              after it sat a column to the left -- Gross Budget under DSP Fee %. */}
          <Table.Summary.Cell index={nextIndex()} />
          <Table.Summary.Cell index={nextIndex()} />
          <Table.Summary.Cell index={nextIndex()} />
          <Table.Summary.Cell index={nextIndex()} />
          {totalCell(formatCurrency(totals.grossBudgetPlan))}
          {totalCell(formatCurrency(totals.netBudgetPlan))}
          <Table.Summary.Cell index={nextIndex()} className="numeric" align="right">
            <strong>
              {totals.impressions.toLocaleString('en-US', { maximumFractionDigits: 0 })}
            </strong>
          </Table.Summary.Cell>
          {totalCell(formatCurrency(totals.publisherSpendPlan))}
          {totalCell(formatCurrency(totals.dspFeeValuePlan))}
          {totalCell(formatCurrency(totals.totalMediaSpendPlan))}
          {totalCell(formatCurrency(totals.allocatedHardCostPlan))}
          {totalCell(formatCurrency(totals.grossMarginPlan))}
          <Table.Summary.Cell index={nextIndex()} />
          {totalCell(formatCurrency(totals.tradingDealAmountPlan))}
          {totalCell(formatCurrency(totals.grossProfitPlan))}
          {totalCell(formatPercent(totals.blendedMarginPercentage))}
          {totalCell(formatPercent(totals.blendedProfitPercentage))}
          {showActions ? <Table.Summary.Cell index={nextIndex()} /> : null}
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  return (
    <TableCard>
      <Table
        columns={columns}
        dataSource={rateCards}
        rowKey="id"
        loading={loading}
        scroll={{ x: 2400, y: 400 }}
        pagination={false}
        size="small"
        summary={renderSummary}
      />
    </TableCard>
  );
};
